import { StatusCellRenderer } from "admin-app/components/cellRenderer";
import CreateSubscriptionForm from "admin-app/components/forms/create-subscription";
import { TOrganizationSubscriptionsLog } from "admin-app/models";
import { ColDef } from "ag-grid-enterprise";
import { AgGridReact } from "ag-grid-react";
import { Button } from "antd";
import { GridLoadingIndicator } from "components/widgets";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  DATE_FORMAT_MMDDYYYY_HHMM_A,
  DateFilter,
  DateUtils
} from "utils/dateutils";
import { Link } from "react-router-dom";
import CiqLogPageHeader, {
  calculateLogPageStats
} from "components/ciq-log-page-header";
import CiqAgSearch from "components/ciq-ag-search";
import { AcceptanceStatus } from "../../../constants";

function DetailCellRenderer({ data }: any) {
  const [detailData, setDetailData] = useState<any[]>([]);

  const detailColumnDefs: ColDef[] = [
    {
      field: "name",
      headerName: "Project Name",
      width: 500
    },
    {
      field: "new_entity_model_enabled",
      headerName: "New Entity Model",
      width: 300,
      cellRenderer: ({ value }: any) => value.toString()
    }
  ];

  useEffect(() => {
    setDetailData(data.subscription.projects_owned);
  }, [data?.subscription?.projects_owned]);

  return (
    <div
      className="ag-theme-alpine"
      style={{
        paddingLeft: "10%",
        paddingRight: "10%",
        height: `${Math.max(100, detailData.length * 25 + 60)}px`,
        width: "100%"
      }}
    >
      <AgGridReact
        columnDefs={detailColumnDefs}
        rowData={detailData}
        domLayout="autoHeight"
        headerHeight={30}
        rowHeight={25}
      />
    </div>
  );
}

function SubscriptionList({
  organization_id,
  organization_subscriptions
}: {
  organization_id: string;
  organization_subscriptions?: Array<TOrganizationSubscriptionsLog>;
}) {
  const gridRef = useRef<AgGridReact<TOrganizationSubscriptionsLog>>(null);
  const [isGridReady, setGridReady] = useState(false);
  const [statsData, setStatsData] = useState<{
    displayCount: number;
    filterName: undefined | string;
  }>();
  const [openAddSubscriptionModel, setOpenAddSubscriptionModel] =
    useState(false);

  const columnDefs: ColDef[] = [
    {
      colId: "id",
      field: "id",
      headerName: "ID",
      headerTooltip: "ID",
      cellRenderer: "agGroupCellRenderer",
      tooltipField: "id",
      width: 100
    },
    {
      colId: "name",
      field: "subscription.name",
      headerName: "Subscription Name",
      tooltipField: "subscription.name",
      // eslint-disable-next-line react/no-unstable-nested-components
      cellRenderer: ({ data, value }: any) => (
        <Link
          to={`/organizations/${organization_id}/subscriptions/${data.subscription.id}/project/`}
          data-testid="subscription-list-link"
        >
          {value}
        </Link>
      )
    },
    {
      colId: "create_by",
      headerName: "Created By",
      valueGetter: ({ data }) => {
        const { subscription } = data;
        return `${subscription.created_by_user.first_name} ${subscription.created_by_user.last_name}`;
      },
      tooltipValueGetter: ({ value }) => value
    },
    {
      colId: "created_at",
      field: "created_at",
      headerName: "Created On",
      comparator: (value1, value2) =>
        DateFilter.customComparator(
          value1,
          value2,
          DATE_FORMAT_MMDDYYYY_HHMM_A
        ),
      valueGetter: ({ data }) => {
        const { subscription } = data;
        return DateUtils.format(
          subscription.created_at,
          DATE_FORMAT_MMDDYYYY_HHMM_A
        );
      },
      tooltipValueGetter: ({ value }) => value
    },
    {
      colId: "subscription_users_email",
      headerName: "Subscription Admin Invited",
      valueGetter: ({ data }) => {
        const subscriptionUsers: Array<any> =
          data.subscription.subscription_users;
        if (subscriptionUsers.length) {
          return subscriptionUsers[0].user.email;
        }
        return "";
      },
      tooltipValueGetter: ({ value }) => value
    },
    {
      colId: "status",
      headerName: "Status",
      valueGetter: ({ data }) => {
        const subscriptionUsers: Array<any> =
          data.subscription.subscription_users;
        if (subscriptionUsers.length) {
          return subscriptionUsers[0].status_id;
        }
        return "";
      },
      getQuickFilterText: ({ value }) => {
        switch (value) {
          case AcceptanceStatus.ACTIVE:
            return "Active";
          case AcceptanceStatus.DEACTIVATED:
            return "Deactivated";

          default:
            return "Pending";
        }
      },
      cellRenderer: StatusCellRenderer
    }
  ];

  const defaultColDef: {} = useMemo(() => {
    return {
      sortable: true,
      editable: false,
      filter: false,
      resizable: true,
      menuTabs: []
    };
  }, []);

  useEffect(() => {
    function handleResize() {
      if (isGridReady && gridRef && gridRef.current) {
        gridRef?.current?.api?.sizeColumnsToFit();
      }
    }
    window.addEventListener("resize", handleResize);
  }, [isGridReady]);

  const onFiltersApplied = () => {
    setStatsData(
      calculateLogPageStats({
        gridRef
      })
    );
  };

  const headerItems = [
    <CiqAgSearch
      key="Subscriptions_search"
      gridRef={gridRef}
      placeholder="Search Subscriptions"
    />,
    <Button
      key="Subscriptions_AddNew"
      className="px-5"
      type="primary"
      onClick={() => setOpenAddSubscriptionModel(true)}
      data-testid="subscription-list-button"
    >
      Create Subscription
    </Button>
  ];

  return (
    <div className="flex flex-col w-full h-[calc(100vh-136px)] bg-white">
      <CiqLogPageHeader
        {...{
          entityName: "Subscriptions",
          titleParam: {
            title: "Subscriptions",
            totalCount: organization_subscriptions?.length,
            filterStats: statsData
          },
          gridRef,
          items: headerItems
        }}
      />
      <div className="grow w-full h-full  ag-theme-alpine">
        <AgGridReact<TOrganizationSubscriptionsLog>
          ref={gridRef}
          rowData={organization_subscriptions}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          headerHeight={36}
          loadingOverlayComponent={GridLoadingIndicator}
          onFilterChanged={onFiltersApplied}
          onGridReady={() => {
            setGridReady(true);
            gridRef.current?.api.sizeColumnsToFit();
          }}
          overlayNoRowsTemplate="There is no subscriptions created yet, please click 'Create Subscription' button to create subscriptions."
          masterDetail
          detailCellRenderer={DetailCellRenderer}
          detailRowHeight={220}
          detailRowAutoHeight
        />
      </div>
      {openAddSubscriptionModel && (
        <CreateSubscriptionForm
          open={openAddSubscriptionModel}
          organization_id={organization_id}
          onClose={() => setOpenAddSubscriptionModel(false)}
        />
      )}
    </div>
  );
}
export default SubscriptionList;
